import { formatMoney } from "@/utils/billingFormat";

export const BOOKING_STATUS_OPTIONS = [
  { value: 0, label: "Inquiry" },
  { value: 1, label: "Pending" },
  { value: 2, label: "Confirmed" },
  { value: 3, label: "Checked in" },
  { value: 4, label: "Checked out" },
  { value: 5, label: "Cancelled" },
  { value: 6, label: "No show" },
];

export const BOOKING_STATUS_TABS = [
  { key: "active", label: "Active", dot: "bg-emerald-500" },
  { key: "pending", label: "Pending", statusValue: 1, dot: "bg-amber-500" },
  { key: "confirmed", label: "Confirmed", statusValue: 2, dot: "bg-blue-500" },
  { key: "checked_in", label: "Checked in", statusValue: 3, dot: "bg-emerald-600" },
  { key: "checked_out", label: "Checked out", statusValue: 4, dot: "bg-slate-400" },
  { key: "cancelled", label: "Cancelled", statusValue: 5, dot: "bg-red-500" },
  { key: "no_show", label: "No show", statusValue: 6, dot: "bg-orange-500" },
  { key: "all", label: "All", dot: null },
];

export const isBookingActive = (status) => {
  const norm = normalizeBookingStatus(status);
  return norm === 0 || norm === 1 || norm === 2 || norm === 3;
};

export const normalizeBookingStatusTab = (status) => {
  if (!status) return "active";
  const str = String(status).trim().toLowerCase().replace(/[\s_-]/g, "");
  switch (str) {
    case "active":
      return "active";
    case "pending":
    case "1":
      return "pending";
    case "confirmed":
    case "2":
      return "confirmed";
    case "checkedin":
    case "3":
      return "checked_in";
    case "checkedout":
    case "4":
      return "checked_out";
    case "cancelled":
    case "canceled":
    case "5":
      return "cancelled";
    case "noshow":
    case "6":
      return "no_show";
    case "inquiry":
    case "0":
      return "inquiry";
    case "all":
      return "all";
    default:
      return "active";
  }
};

export const matchesBookingStatusTab = (status, tabKey) => {
  const normalizedTab = normalizeBookingStatusTab(tabKey);
  if (normalizedTab === "all") return true;
  if (normalizedTab === "active") return isBookingActive(status);

  const norm = normalizeBookingStatus(status);
  switch (normalizedTab) {
    case "pending":
      return norm === 1;
    case "confirmed":
      return norm === 2;
    case "checked_in":
      return norm === 3;
    case "checked_out":
      return norm === 4;
    case "cancelled":
      return norm === 5;
    case "no_show":
      return norm === 6;
    case "inquiry":
      return norm === 0;
    default:
      return true;
  }
};

export const getBookingStatusTabApiValue = (tabKey) => {
  const normalizedTab = normalizeBookingStatusTab(tabKey);
  switch (normalizedTab) {
    case "active":
      return "active";
    case "pending":
      return 1;
    case "confirmed":
      return 2;
    case "checked_in":
      return 3;
    case "checked_out":
      return 4;
    case "cancelled":
      return 5;
    case "no_show":
      return 6;
    case "inquiry":
      return 0;
    case "all":
    default:
      return undefined;
  }
};

export const BOOKING_SOURCE_OPTIONS = [
  { value: 1, label: "Walk-in" },
  { value: 2, label: "Phone" },
  { value: 3, label: "Website" },
  { value: 4, label: "Reception" },
  { value: 5, label: "Travel agent" },
  { value: 6, label: "OTA" },
  { value: 7, label: "Manual entry" },
  { value: 8, label: "Other" },
];

export const BOOKING_PAYMENT_METHOD_OPTIONS = [
  { value: 1, label: "Cash" },
  { value: 2, label: "Card" },
  { value: 3, label: "Bank transfer" },
  { value: 4, label: "Online" },
  { value: 5, label: "Other" },
];

export const ROOM_STATUS_CONFIG = {
  0: { label: "Available", badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200", canCheckIn: true, description: "Room is clean, inspected, and ready for check-in." },
  1: { label: "Occupied", badgeClass: "bg-blue-50 text-blue-700 border-blue-200", canCheckIn: false, description: "Room is currently occupied by an active guest session." },
  2: { label: "Cleaning", badgeClass: "bg-amber-50 text-amber-700 border-amber-200", canCheckIn: false, description: "Housekeeping is currently cleaning this room." },
  3: { label: "Maintenance", badgeClass: "bg-orange-50 text-orange-700 border-orange-200", canCheckIn: false, description: "Room is undergoing maintenance." },
  4: { label: "Inactive", badgeClass: "bg-slate-100 text-slate-600 border-slate-200", canCheckIn: false, description: "Room is marked inactive." },
  5: { label: "Dirty", badgeClass: "bg-rose-50 text-rose-700 border-rose-200", canCheckIn: false, description: "Room is dirty and requires housekeeping cleaning and inspection." },
  6: { label: "Inspected", badgeClass: "bg-indigo-50 text-indigo-700 border-indigo-200", canCheckIn: true, description: "Room has been inspected and is ready for check-in." },
  7: { label: "Out of Order", badgeClass: "bg-purple-50 text-purple-700 border-purple-200", canCheckIn: false, description: "Room is out of order." },
  8: { label: "Blocked", badgeClass: "bg-red-50 text-red-700 border-red-200", canCheckIn: false, description: "Room is blocked." },
  9: { label: "Reserved", badgeClass: "bg-sky-50 text-sky-700 border-sky-200", canCheckIn: true, description: "Room is reserved." },
  Available: { label: "Available", badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200", canCheckIn: true, description: "Room is clean, inspected, and ready for check-in." },
  Occupied: { label: "Occupied", badgeClass: "bg-blue-50 text-blue-700 border-blue-200", canCheckIn: false, description: "Room is currently occupied by an active guest session." },
  Cleaning: { label: "Cleaning", badgeClass: "bg-amber-50 text-amber-700 border-amber-200", canCheckIn: false, description: "Housekeeping is currently cleaning this room." },
  Maintenance: { label: "Maintenance", badgeClass: "bg-orange-50 text-orange-700 border-orange-200", canCheckIn: false, description: "Room is undergoing maintenance." },
  Inactive: { label: "Inactive", badgeClass: "bg-slate-100 text-slate-600 border-slate-200", canCheckIn: false, description: "Room is marked inactive." },
  Dirty: { label: "Dirty", badgeClass: "bg-rose-50 text-rose-700 border-rose-200", canCheckIn: false, description: "Room is dirty and requires housekeeping cleaning and inspection." },
  Inspected: { label: "Inspected", badgeClass: "bg-indigo-50 text-indigo-700 border-indigo-200", canCheckIn: true, description: "Room has been inspected and is ready for check-in." },
  OutOfOrder: { label: "Out of Order", badgeClass: "bg-purple-50 text-purple-700 border-purple-200", canCheckIn: false, description: "Room is out of order." },
  Blocked: { label: "Blocked", badgeClass: "bg-red-50 text-red-700 border-red-200", canCheckIn: false, description: "Room is blocked." },
  Reserved: { label: "Reserved", badgeClass: "bg-sky-50 text-sky-700 border-sky-200", canCheckIn: true, description: "Room is reserved." },
};

export const getRoomStatusConfig = (status) => {
  if (status === null || status === undefined || status === "") return null;
  return ROOM_STATUS_CONFIG[status] || {
    label: String(status),
    badgeClass: "bg-slate-100 text-slate-700 border-slate-200",
    canCheckIn: false,
    description: `Room status is ${status}.`,
  };
};

export const isRoomReadyForCheckIn = (status) => {
  if (status === null || status === undefined || status === "") return true; // fallback if unknown
  const config = getRoomStatusConfig(status);
  return config ? config.canCheckIn : true;
};

export const getApiData = (response) =>
  response?.data?.data || response?.data?.Data || response?.data || response || null;

export const getApiMessage = (error, fallback = "Something went wrong.") =>
  error?.response?.data?.message ||
  error?.response?.data?.Message ||
  error?.response?.data?.detail ||
  error?.response?.data?.Detail ||
  error?.response?.data?.title ||
  error?.message ||
  fallback;


export const toLocalDateInputValue = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value || "").slice(0, 10);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return toLocalDateInputValue(date);
};

export const formatBookingDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB").format(date);
};

export const calculateNights = (checkInDate, checkOutDate) => {
  if (!checkInDate || !checkOutDate) return 0;
  const checkIn = new Date(`${checkInDate}T00:00:00`);
  const checkOut = new Date(`${checkOutDate}T00:00:00`);
  if (Number.isNaN(checkIn.getTime()) || Number.isNaN(checkOut.getTime())) return 0;
  return Math.max(0, Math.round((checkOut - checkIn) / 86400000));
};

export const normalizeBookingStatus = (status) => {
  if (status === null || status === undefined || status === "") return 1;
  if (typeof status === "number") {
    return Number.isFinite(status) ? status : 1;
  }
  const str = String(status).trim();
  const num = Number(str);
  if (!Number.isNaN(num) && Number.isFinite(num)) {
    return num;
  }
  const clean = str.replace(/[\s_-]/g, "").toLowerCase();
  switch (clean) {
    case "inquiry":
      return 0;
    case "pending":
      return 1;
    case "confirmed":
      return 2;
    case "checkedin":
      return 3;
    case "checkedout":
      return 4;
    case "cancelled":
    case "canceled":
      return 5;
    case "noshow":
      return 6;
    default:
      return 1;
  }
};

export const normalizeBookingSource = (source) => {
  if (source === null || source === undefined || source === "") return 4;
  if (typeof source === "number") {
    return Number.isFinite(source) ? source : 4;
  }
  const str = String(source).trim();
  const num = Number(str);
  if (!Number.isNaN(num) && Number.isFinite(num)) {
    return num;
  }
  const clean = str.replace(/[\s_-]/g, "").toLowerCase();
  switch (clean) {
    case "walkin":
      return 1;
    case "phone":
      return 2;
    case "website":
      return 3;
    case "reception":
      return 4;
    case "travelagent":
      return 5;
    case "ota":
      return 6;
    case "manualentry":
      return 7;
    case "other":
      return 8;
    default:
      return 4;
  }
};

export const getBookingStatusLabel = (status) => {
  const norm = normalizeBookingStatus(status);
  const match = BOOKING_STATUS_OPTIONS.find((item) => item.value === norm);
  return match?.label || "Pending";
};

export const getBookingStatusBadgeClass = (status) => {
  const label = getBookingStatusLabel(status);
  switch (label) {
    case "Confirmed":
      return "border-blue-200 bg-blue-50 text-blue-700";
    case "Checked in":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "Checked out":
      return "border-slate-200 bg-slate-50 text-slate-700";
    case "Cancelled":
      return "border-red-200 bg-red-50 text-red-700";
    case "No show":
      return "border-orange-200 bg-orange-50 text-orange-700";
    case "Inquiry":
      return "border-purple-200 bg-purple-50 text-purple-700";
    case "Pending":
    default:
      return "border-amber-200 bg-amber-50 text-amber-800";
  }
};

export const getBookingSourceLabel = (source) => {
  const norm = normalizeBookingSource(source);
  const match = BOOKING_SOURCE_OPTIONS.find((item) => item.value === norm);
  return match?.label || "Reception";
};

export const getBookingPaymentMethodLabel = (method) => {
  const normalized = String(method ?? "")
    .replace(/[\s_-]/g, "")
    .toLowerCase();
  const match = BOOKING_PAYMENT_METHOD_OPTIONS.find(
    (item) =>
      String(item.value) === String(method) ||
      item.label.replace(/\s/g, "").toLowerCase() === normalized
  );
  return match?.label || String(method || "Cash").replace(/([a-z])([A-Z])/g, "$1 $2");
};

export const getAvailabilityStatusLabel = (status) => {
  const labels = {
    Available: "Available",
    Reserved: "Reserved",
    CheckedIn: "Checked in",
    CheckedOut: "Checked out",
    ExpCheckOut: "Exp-CheckOut",
    "Exp-CheckOut": "Exp-CheckOut",
    ExpectedCheckOut: "Exp-CheckOut",
    Blocked: "Blocked",
    Dirty: "Dirty",
    Cleaning: "Cleaning",
    OutOfOrder: "Out of order",
    Maintenance: "Maintenance",
    Inspected: "Inspected",
  };
  if (labels[status]) return labels[status];
  const value = String(status || "Available");
  const normalized = value.toLowerCase().replace(/[\s_-]/g, "");
  if (normalized === "expcheckout" || normalized === "expectedcheckout") {
    return "Exp-CheckOut";
  }
  return value
    .replace("CheckedIn", "Checked in")
    .replace("CheckedOut", "Checked out")
    .replace("OutOfOrder", "Out of order")
    .replace(/([a-z])([A-Z])/g, "$1 $2");
};

const firstNonEmpty = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") ?? "";

export const normalizeBooking = (booking = {}) => ({
  id: booking.id || booking.Id,
  bookingNumber:
    booking.bookingNumber ||
    booking.BookingNumber ||
    booking.bookingReference ||
    booking.BookingReference ||
    "",
  guestName: firstNonEmpty(booking.guestProfile?.fullName, booking.GuestProfile?.FullName, booking.guestName, booking.GuestName),
  guestPhone: firstNonEmpty(booking.guestProfile?.phoneNumber, booking.GuestProfile?.PhoneNumber, booking.guestPhone, booking.GuestPhone),
  guestEmail: firstNonEmpty(booking.guestProfile?.email, booking.GuestProfile?.Email, booking.guestEmail, booking.GuestEmail),
  guestAddress: firstNonEmpty(booking.guestProfile?.address, booking.GuestProfile?.Address, booking.guestAddress, booking.GuestAddress),
  country: firstNonEmpty(booking.guestProfile?.country, booking.GuestProfile?.Country, booking.country, booking.Country),
  issuingCountry: firstNonEmpty(
    booking.issuingCountry,
    booking.IssuingCountry,
    booking.guestProfile?.country,
    booking.GuestProfile?.Country,
    booking.guestProfile?.nationality,
    booking.GuestProfile?.Nationality,
    booking.nationality,
    booking.Nationality
  ),
  nationality: firstNonEmpty(booking.guestProfile?.nationality, booking.GuestProfile?.Nationality, booking.nationality, booking.Nationality),
  documentType: firstNonEmpty(booking.guestProfile?.documentType, booking.GuestProfile?.DocumentType, booking.documentType, booking.DocumentType, booking.idDocumentType, booking.IdDocumentType),
  documentNumber: firstNonEmpty(booking.guestProfile?.documentNumber, booking.GuestProfile?.DocumentNumber, booking.documentNumber, booking.DocumentNumber, booking.idDocumentNumber, booking.IdDocumentNumber),
  idDocumentType: firstNonEmpty(booking.guestProfile?.documentType, booking.GuestProfile?.DocumentType, booking.idDocumentType, booking.IdDocumentType, booking.documentType, booking.DocumentType),
  idDocumentNumber: firstNonEmpty(booking.guestProfile?.documentNumber, booking.GuestProfile?.DocumentNumber, booking.idDocumentNumber, booking.IdDocumentNumber, booking.documentNumber, booking.DocumentNumber),
  dateOfBirth: toDateInputValue(firstNonEmpty(booking.guestProfile?.dateOfBirth, booking.GuestProfile?.DateOfBirth, booking.dateOfBirth, booking.DateOfBirth, booking.dob, booking.Dob, booking.DOB)),
  gender: firstNonEmpty(booking.guestProfile?.gender, booking.GuestProfile?.Gender, booking.gender, booking.Gender),
  guestProfileId: firstNonEmpty(booking.guestProfileId, booking.GuestProfileId, booking.guestProfile?.id, booking.GuestProfile?.Id),
  guestProfile: booking.guestProfile || booking.GuestProfile || null,
  continueAsAnonymous: !(booking.guestProfileId || booking.GuestProfileId || booking.guestProfile?.id || booking.GuestProfile?.Id),
  inviteGuestToRegister: false,
  bookerProfileId: booking.bookerProfileId || booking.BookerProfileId || "",
  bookerName: booking.bookerName || booking.BookerName || "",
  bookerType: booking.bookerType ?? booking.BookerType ?? "",
  bookerReferenceNumber: booking.bookerReferenceNumber || booking.BookerReferenceNumber || "",
  commissionOrRewardEligible: booking.commissionOrRewardEligible ?? booking.CommissionOrRewardEligible ?? false,
  roomTypeId: booking.roomTypeId || booking.RoomTypeId || "",
  roomTypeName: booking.roomTypeName || booking.RoomTypeName || "",
  roomTypeCode: booking.roomTypeCode || booking.RoomTypeCode || "",
  roomId: booking.roomId || booking.RoomId || "",
  roomName: booking.roomName || booking.RoomName || "",
  roomStatus: booking.roomStatus ?? booking.RoomStatus ?? null,
  roomStatusName: booking.roomStatusName ?? booking.RoomStatusName ?? null,
  guestSessionId: booking.guestSessionId || booking.GuestSessionId || null,
  checkInDate: toDateInputValue(booking.checkInDate || booking.CheckInDate),
  checkOutDate: toDateInputValue(booking.checkOutDate || booking.CheckOutDate),
  adults: Number(booking.adults ?? booking.Adults ?? 1),
  children: Number(booking.children ?? booking.Children ?? 0),
  bookingSource: normalizeBookingSource(booking.bookingSource ?? booking.BookingSource),
  status: normalizeBookingStatus(booking.status ?? booking.Status),
  ratePerNight: Number(booking.ratePerNight ?? booking.RatePerNight ?? 0),
  discountAmount: Number(booking.discountAmount ?? booking.DiscountAmount ?? 0),
  taxAmount: Number(booking.taxAmount ?? booking.TaxAmount ?? 0),
  serviceChargeAmount: Number(booking.serviceChargeAmount ?? booking.ServiceChargeAmount ?? 0),
  depositAmount: Number(booking.depositAmount ?? booking.DepositAmount ?? 0),
  notes: booking.notes || booking.Notes || "",
  specialRequests: booking.specialRequests || booking.SpecialRequests || "",
  isWalkIn: booking.isWalkIn ?? booking.IsWalkIn ?? false,
  nights: Number(booking.nights ?? booking.Nights ?? 0),
  totalGuests: Number(booking.totalGuests ?? booking.TotalGuests ?? 0),
  totalAmount: Number(booking.totalAmount ?? booking.TotalAmount ?? 0),
  paidAmount: Number(booking.paidAmount ?? booking.PaidAmount ?? 0),
  dueAmount: Number(booking.dueAmount ?? booking.DueAmount ?? 0),
  payments: booking.payments || booking.Payments || [],
  auditLogs: booking.auditLogs || booking.AuditLogs || [],
  cancelledAtUtc: booking.cancelledAtUtc || booking.CancelledAtUtc || null,
  cancellationReason: booking.cancellationReason || booking.CancellationReason || "",
});

export { formatMoney };
